/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import NoResults from './NoResults';
import MedliSearchResult from './MedliSearchResult';
import List, { ListItem } from '../../../components/List';
import { Grid } from '../../../components/Grid';
import { NoCardsPaddedContent } from '../../../components/PaddedContent';
import ContentWrapper from '../../../components/ContentWrapper';
import SearchInfoModal from '../../../components/modal/SearchInfoModal';

import { Card } from '../../../../bphComponents/bundle';
import { useUIState } from '../../../hooks/useUIState';

interface Value {
  searchResults: any[];
  header?: any;
  footer?: any;
}

interface Props {
  isSearching?: boolean;
  isSearchComplete: boolean;
  query: string;
  values: Value[];
  onSelectSearchResult?(sr, i): void;
}

const SearchResults: React.FC<Props> = (props) => {
  const { isSearching, isSearchComplete, values, query, onSelectSearchResult } = props;
  const hasResults = values.reduce((val, v) => (val || v.searchResults.length > 0), false);
  const showNoResults = !hasResults && isSearchComplete;

  const [hasSelected, setHasSelected] = React.useState(false);

  const [, uiActions] = useUIState();

  return (
    <div className="cmp-search-results">
      {
        showNoResults ? (
          <NoCardsPaddedContent>
            <NoResults query={query} />
          </NoCardsPaddedContent>
        ) : (
          <ContentWrapper>
            {
              isSearching ? (
                <div className="font-caption-1 font-color-gsMedDark util-application-card-padding" style={{ fontStyle: 'italic' }}>
                  <FormattedMessage id="search.searching.message.text" />
                </div>
              ) : null
            }
            {
              values.map((value: Value, i: number) => {
                const { searchResults, header, footer } = value;
                const hasHeader = header && !!searchResults.length;
                const lastIndex = searchResults.length - 1;
                return (
                  <div key={i}>
                    {
                      hasHeader
                      ? <Card.ExternalCardTitle>{header}</Card.ExternalCardTitle>
                      : null
                    }
                    <List hasRoundedWrapper paddingSize="default">
                      {
                        searchResults.map((r, index) => (
                          <ListItem
                            key={index}
                            hasBottomBorder
                            onClick={() => {
                              if (hasSelected) { return; }
                              setHasSelected(true);
                              onSelectSearchResult(r, index);
                            }}
                          >
                            <Grid alignItems="center">
                              <MedliSearchResult
                                searchResult={r.searchResult}
                                isLastResult={lastIndex === index}
                                openLongDefinition={(sr) => {
                                  uiActions.showNotLoaded(<SearchInfoModal searchResult={sr} />);
                                }}
                              />
                            </Grid>
                          </ListItem>
                        ))
                      }
                    </List>
                    {
                      footer ? (
                        <>
                        <div style={{ marginTop: 16 }} />
                        <Card.ExternalContent noMarginTop={true}>
                          {footer}
                        </Card.ExternalContent>
                        </>
                      ) : null
                    }
                  </div>
                );
              })
            }
          </ContentWrapper>
        )
      }
    </div>
  );
};

export default SearchResults;
